import React from 'react'
import moment from 'moment'
// @src imports
import { Icon } from 'src/chrome'
import { IconTypeOrIconString } from 'src/app/types'

import styles from './reminder.module.scss'
import { Flex, Text, Button, Anchor } from '@sendoutcards/quantum-design-ui'
import CloseButton from 'src/chrome/CloseButton/CloseButton'
import { Maybe, MinimalReminderFragment } from 'src/graphql/generated/graphql'
import { useMemo } from 'src/hooks'

const gradients = {
  pink: 'linear-gradient(115deg, #ff5689, #e687ff)',
  blue: 'linear-gradient(115deg, #6db0ff, #7de8f6)',
  orange: 'linear-gradient(28deg, #DD2476 0%, #FE5030 100%)',
}

const getGreetings = (name: string, type: string) => {
  const list = [
    `Wish ${name} a happy ${type}`,
    `Celebrate with ${name}`,
    `${name}'s ${type} is coming up!`,
  ]

  const greeting = list[Math.floor(Math.random() * list.length)]
  return greeting
}

const properties = (
  reminder: MinimalReminderFragment,
): {
  color: keyof typeof gradients
  icon: IconTypeOrIconString
  iconColor: string
  viewBox: string
} => {
  if (reminder.newType === 'ANNIVERSARY') {
    return {
      color: 'pink',
      icon: 'CHEERS',
      iconColor: '#F758A1',
      viewBox: '0 0 33 32',
    }
  } else if (reminder.newType === 'BIRTHDAY') {
    return {
      color: 'blue',
      icon: 'CONFETTI',
      iconColor: '#6db0ff',
      viewBox: '0 0 31 32',
    }
  } else {
    return {
      color: 'orange',
      icon: 'CAMPAIGNS',
      iconColor: '#DD2476',
      viewBox: '0 0 25 32',
    }
  }
}

interface ReminderProps {
  onDismiss: (reminder: MinimalReminderFragment) => void
  onSendCard: (
    contact: {
      id: string
      firstName: string
      lastName: string
      birthday?: Maybe<{
        day: number
        month: number
      }>
      anniversary?: Maybe<{
        day: number
        month: number
      }>
    },
    reminderId: string,
  ) => void
  openCampaign: (campaignId: string) => void
  onOpenContact: (contactId: string) => void
  reminder: MinimalReminderFragment
  openOrder: (orderId: string) => void
  onOpenHistory: (orderId: string, contactId: string) => void
}

const Reminder: React.FC<ReminderProps> = props => {
  const {
    onDismiss,
    onSendCard,
    openCampaign,
    onOpenContact,
    reminder,
    openOrder,
    onOpenHistory,
  } = props
  const { campaign } = reminder
  const { icon, viewBox, iconColor } = properties(reminder)

  const celebrationDate =
    reminder.type === 'BIRTHDAY'
      ? {
          day: reminder.contact.birthday?.day,
          month: reminder.contact.birthday?.month,
        }
      : {
          day: reminder.contact.anniversary?.day,
          month: reminder.contact.anniversary?.month,
        }

  const getCelebrationDateYear = (
    currentMonthIndex: number,
    celebrationMonthIndex: number,
    currentYear: number,
  ) => {
    const celebrationYear =
      currentMonthIndex > celebrationMonthIndex ? currentYear : currentYear + 1
    return celebrationYear
  }

  const isMailDeliveryTimeSufficient = (
    celebrationDay: number | undefined,
    celebrationMonth: number | undefined,
  ) => {
    const isUSContact =
      reminder.contact.country === 'United States' ? true : false
    const daysLimit = isUSContact ? 7 : 14

    if (celebrationDay !== undefined && celebrationMonth !== undefined) {
      // month index adjustment
      const celebrationMonthIndex = celebrationMonth - 1
      const today = new Date()
      const celebrationDate = new Date(
        getCelebrationDateYear(
          today.getMonth(),
          celebrationMonthIndex,
          today.getFullYear(),
        ),
        celebrationMonthIndex,
        celebrationDay,
      )

      if (
        Math.round((celebrationDate.getTime() - today.getTime()) / 86400000) <
        daysLimit
      ) {
        return false
      }
    }
    return true
  }

  const capitalizeFirstLetter = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1)

  const greetings = useMemo(() => {
    return getGreetings(reminder.contact.firstName, reminder.title)
  }, [reminder.contact.firstName, reminder.title])

  return (
    <Flex
      width="100%"
      height="100%"
      outset={{ vertical: 'x1' }}
      inset="x2"
      position="relative"
      borderRadius="default"
      backgroundColor="foreground"
      boxShadow="light"
    >
      {campaign === null || campaign === undefined ? (
        <Flex width="100%" flexDirection="column" justifyContent="flex-start">
          <Flex width="100%" flexDirection="column" justifyContent="flex-start">
            <Flex alignItems="center" columnGap="x3">
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Icon
                  icon={icon}
                  viewBox={viewBox}
                  color={iconColor}
                  size={35}
                />
                <Text
                  type="footnote"
                  color="accent"
                  alignment="center"
                  whiteSpace="nowrap"
                  style={{ color: iconColor }}
                >
                  {reminder.eventDate
                    ? moment(reminder.eventDate).format('MMM Do')
                    : reminder.newType === 'CAMPAIGN'
                      ? 'Campaign'
                      : null}
                </Text>
              </Flex>
              <Flex flexDirection="column">
                <Text
                  type="body"
                  weight="bold"
                  content={`${reminder.contact.firstName} ${reminder.contact.lastName}`}
                  color="primaryHeading"
                />
                <Text
                  type="caption"
                  color="primaryHeading"
                  lineHeight={1}
                  content={capitalizeFirstLetter(reminder.title)}
                />
              </Flex>
            </Flex>
            <Flex>
              {!isMailDeliveryTimeSufficient(
                celebrationDate.day,
                celebrationDate.month,
              ) && (
                <Text type="caption" color="danger">
                  Hurry and send a card, mail delivery times may not be
                  sufficient.
                </Text>
              )}
              <Text
                type="caption"
                content={greetings}
                inset={{ vertical: 'x1' }}
              />
              <Text type="caption">{reminder.message ?? ''}</Text>
            </Flex>
            <Flex
              justifyContent="end"
              columnGap="x3"
              alignItems="start"
              outset={{ right: 'x1_5' }}
            >
              <Anchor
                size="xSmall"
                title={'Open Contact'}
                onClick={() => onOpenContact(reminder.contact.id)}
                isDecorated
                whileFocus={{ scale: 1.5 }}
              />
              {!reminder.order && (
                <Button
                  size="xSmall"
                  backgroundColorOverride="#404040"
                  title={'Send a Card'}
                  onClick={() => onSendCard(reminder.contact, reminder.id)}
                />
              )}
              {reminder.order && !reminder.order.isSent && (
                <Button
                  size="xSmall"
                  title={'Continue Order'}
                  onClick={() =>
                    openOrder(reminder.order ? reminder.order.id : '')
                  }
                />
              )}
              {reminder.order && reminder.order.isSent && (
                <Button
                  size="xSmall"
                  title={'Card History'}
                  onClick={() =>
                    onOpenHistory(
                      reminder.order ? reminder.order?.id : '',
                      reminder.contact.id,
                    )
                  }
                />
              )}
            </Flex>
          </Flex>
        </Flex>
      ) : (
        <div className={styles.body}>
          <div className={styles.details} style={{ flexGrow: 1 }}>
            <Text type="largeBody" color="primaryHeading">
              {campaign.name}
            </Text>
          </div>
          <Button
            title={'View Campaign'}
            onClick={() => openCampaign(campaign.id)}
          />
        </div>
      )}
      <Flex alignItems="center" position="absolute" top="8px" right="8px">
        <CloseButton size={24} onClose={() => onDismiss(reminder)} />
      </Flex>
    </Flex>
  )
}

export default Reminder
